import React, { FunctionComponent, useEffect, useMemo, useState, useRef, ReactNode } from 'react';
import { captureException } from '@sentry/nextjs';
import { PillowTalkProvider, useStartConversation } from '@spotify-internal/pillow-talk';

import { Vertical } from 'utils/vertical';
import { useVerticalConfig } from 'providers/vertical';
import { sendCounterMetric, Whats } from 'utils/metrics';
import { isPillowTalkSupportedLanguageCode } from 'utils/type-guards';
import { SUPPORT_SITE_CLIENT_ID } from 'utils/config';

import {
  DEFAULT_STATUS,
  DISABLED_STATUS,
  LineOfBusiness,
  MessagingAvailabilityReason,
  MessagingType,
  SupportedLanguageCode,
} from './types';
import { MessagingContext } from './MessagingContext';

interface PillowTalkMessagingProviderProps {
  vertical: Vertical;
  lineOfBusiness: LineOfBusiness;
  locale: string;
  children: ReactNode;
}

const PillowTalkMessagingProvider = ({
  vertical,
  lineOfBusiness,
  locale,
  children,
}: PillowTalkMessagingProviderProps) => {
  const shouldSetStatus = useRef(true);

  const [status, setStatus] = useState(DEFAULT_STATUS);

  const lang: SupportedLanguageCode = isPillowTalkSupportedLanguageCode(locale) ? locale : 'en-US';

  const { startConversation, loading, error } = useStartConversation(lineOfBusiness, {
    lang,
  });

  const value = useMemo<MessagingType>(
    () => ({
      status,
      startMessaging: status.isAvailable
        ? async () => {
            try {
              await startConversation();
              sendCounterMetric(Whats.PILLOW_TALK_MESSAGING_START, { vertical });
            } catch (err) {
              captureException(err);
            }
          }
        : undefined,
    }),
    [status, startConversation, vertical],
  );

  useEffect(() => {
    if (!shouldSetStatus.current || loading) {
      return;
    }

    if (!error) {
      const resolution = MessagingAvailabilityReason.AVAILABLE;
      setStatus({ isAvailable: true, reason: resolution });
      sendCounterMetric(Whats.PILLOW_TALK_MESSAGING_SETUP, { resolution, vertical });
    } else {
      const resolution = MessagingAvailabilityReason.PILLOW_TALK_ERROR;
      setStatus({ isAvailable: false, reason: resolution });
      sendCounterMetric(Whats.PILLOW_TALK_MESSAGING_SETUP, { resolution, vertical });
    }
    shouldSetStatus.current = false;
  }, [loading, error, vertical]);

  return <MessagingContext.Provider value={value}>{children}</MessagingContext.Provider>;
};

export const PillowTalkWrapper: FunctionComponent<{ locale: string }> = ({ children, locale }) => {
  const {
    verticalName: vertical,
    features: { configurePillowTalk },
  } = useVerticalConfig();

  if (!configurePillowTalk) {
    return <MessagingContext.Provider value={{ status: DISABLED_STATUS }}>{children}</MessagingContext.Provider>;
  }

  const { lineOfBusiness, redirectUri } = configurePillowTalk;

  return (
    <PillowTalkProvider clientId={SUPPORT_SITE_CLIENT_ID} redirectUri={redirectUri}>
      <PillowTalkMessagingProvider vertical={vertical} lineOfBusiness={lineOfBusiness} locale={locale}>
        {children}
      </PillowTalkMessagingProvider>
    </PillowTalkProvider>
  );
};
